// "Request withdrawal" (contract §9 pins path, default export and the `balance` /
// `minPayoutCents` props). POST /api/tenant/gateway/payouts is NOT one of the §4.1
// step-up-gated endpoints (that gate is super-admin-only), so this form's confirm dialog
// is the ordinary Radix confirmation pattern — no identity step.
//
// `minPayoutCents` comes from `balance.limits.minPayoutCents` (GET .../gateway/balance,
// projected from `gateway_settings`) — the caller (`GatewayWallet`) reads it off the
// balance response and passes it through. When it is `null`/`undefined` (an older
// backend, or `limits` absent entirely), the floor check below is DISABLED rather than
// substituting a guessed number: an absent limit must never be shown to a practitioner
// as if it were a real one. The hard ceiling this form owns outright regardless is
// `amount <= balance.availableCents` — that number is never absent, and there is no
// error path for it: the submit button is simply disabled.
import React, { useState } from "react";
import { toast } from "sonner";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { dateLocale } from "../../i18n/dateLocale";
import { fmtMoney, toCents } from "../../admin/money";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";
import { SETTLEMENT_CURRENCY } from "../contract";
import { estimatePayoutConversion } from "../payoutConversion";
import { intlLocaleFor } from "../../lib/formatMoney";
import { gatewayErrorText } from "../errorText";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Loader2, Send } from "lucide-react";

/**
 * @param {import("../contract").TenantBalance} balance
 * @param {?number} [minPayoutCents] absent/null disables the minimum check, never a guess
 * @param {?string} [payoutSetupHref] where the practitioner connects the destination;
 *   absent = this mount has no such surface, so the warning renders without a link
 * @param {function} [onSubmitted] (payoutRequest) => void — called after a successful POST
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function WithdrawalForm({ balance, minPayoutCents, payoutSetupHref = null, onSubmitted = () => {}, gatewayApi = defaultGatewayApi }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [amountInput, setAmountInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrorCode, setSubmitErrorCode] = useState(null);

  // The balance is by construction in the settlement currency; the constant is the
  // contract's own mirror of it, never a stale literal.
  const currency = balance?.currency || SETTLEMENT_CURRENCY;
  const availableCents = balance?.availableCents ?? 0;
  const autoApproveMaxCents = balance?.limits?.autoApproveMaxCents;
  const amountCents = toCents(amountInput);
  const blocked = !!balance?.frozen;

  // CAN THIS PRACTICE BE PAID AT ALL — asked before the practitioner types (BUG-222).
  // The sheet's case: an unfinished Stripe onboarding, an enabled button, a confirmation
  // dialog promising the money would be sent, and `PAYOUT_DESTINATION_NOT_READY` only
  // after Confirm. The refusal is the server's and stays there; what was missing is that
  // the same fact was already knowable at render time.
  //
  // THE SERVER'S VERDICT, NOT AN INFERENCE. `limits.payoutDestinationReady` is
  // `payouts.validate_payout_eligibility`'s own predicate reported
  // (`modules/payments/connect_readiness.py`); nothing on this payload lets a client
  // re-derive it, and a client guessing from an indirect signal would either block a
  // healthy practitioner or promise a broken one.
  //
  // `undefined` IS NOT `false` — the rule `StripeConnectCard` follows for the same
  // verdict. A payload without the field is a server that does not compute readiness,
  // and reading silence as a blockage would disable withdrawals for every healthy
  // practitioner on the deploy that shipped this.
  //
  // A FREEZE OUTRANKS IT: a frozen practice is refused whatever their Stripe account
  // looks like, and two rose notes stacked on one dead control is how a money screen
  // stops being read.
  const destinationBlocked = !blocked && balance?.limits?.payoutDestinationReady === false;
  const destinationBlockedReason = destinationBlocked
    ? balance?.limits?.payoutDestinationBlockedReason : null;

  const hasMinimum = minPayoutCents != null;
  const belowMinimum = hasMinimum && amountCents > 0 && amountCents < minPayoutCents;
  const aboveAvailable = amountCents > availableCents;
  const validAmount = amountCents > 0 && !belowMinimum && !aboveAvailable;
  // Informational, not a validation error: the request still goes through, it just won't
  // auto-approve. Real expectation-setting only when the platform actually publishes a
  // ceiling — an absent one says nothing about review speed, so no note is shown.
  const expectsReview = autoApproveMaxCents != null && amountCents > autoApproveMaxCents;

  // WHAT THE PRACTITIONER WILL ACTUALLY RECEIVE, before they confirm (spec §3/§8/§12).
  // The rolling reserve comes OUT of the requested amount, so the number typed is not
  // the number that arrives; a dialog echoing only that number is the polite way to
  // mislead. `reserveWindowEndsAt` is the server's single answer to "would anything be
  // held right now" — null covers no money history, an elapsed window and a 0 % rate at
  // once, so this form never re-derives the rule.
  //
  // THE ARITHMETIC IS A PREVIEW, NOT AN AUTHORITY. It mirrors the server's half-up
  // `_mul_div_half_up(amount, pct, 100)` so the figures agree to the cent, and the row
  // the server writes remains the fact. The amount is disclosed again on the response.
  const reservePct = balance?.limits?.rollingReservePct;
  const reserveWindowEndsAt = balance?.limits?.reserveWindowEndsAt;
  const disclosesReserve = reserveWindowEndsAt != null
    && Number.isFinite(reservePct) && reservePct > 0;
  const reserveHeldCents = disclosesReserve
    ? Math.round((amountCents * reservePct) / 100) : 0;
  const receiveNowCents = amountCents - reserveHeldCents;
  // Stripe's cross-border transfer fee is borne by the tenant and is COPIED from the
  // transfer's balance transaction afterwards, never computed — so the note states that
  // a fee applies and quotes no amount. Rendered only when the server says so: a client
  // guessing at the destination country would be inventing a charge.
  const crossBorder = balance?.limits?.crossBorder === true;
  // THE CONVERSION AT PAYOUT (SHEET-212). A practice pricing in its own currency is paid
  // out of the settlement balance and Stripe converts on the destination side, for a
  // fee. The ratio (the practice's own captures, `fx.py`) and the fee rate are the
  // SERVER's statement; this form only does the half-up arithmetic on its integers, on
  // what actually leaves the balance. Null = nothing converts, so nothing is said.
  const conversion = estimatePayoutConversion(
    receiveNowCents, balance?.limits?.payoutConversion);
  const conversionPct = conversion
    ? new Intl.NumberFormat(intlLocaleFor(lang), { maximumFractionDigits: 2 })
      .format(conversion.feeBps / 100)
    : null;
  const localCode = conversion ? String(conversion.currency).toUpperCase() : "";

  const openConfirm = () => {
    if (!validAmount || blocked || destinationBlocked) return;
    setSubmitErrorCode(null);
    setDialogOpen(true);
  };

  const confirm = async () => {
    setSubmitting(true);
    setSubmitErrorCode(null);
    try {
      const result = await gatewayApi.requestPayout({ amountCents });
      toast.success(
        result.status === "approved"
          ? t("gw.wallet.withdrawalApproved")
          : t("gw.wallet.withdrawalRequested"),
      );
      setDialogOpen(false);
      setAmountInput("");
      onSubmitted(result);
    } catch (e) {
      setSubmitErrorCode(extractGatewayErrorCode(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="withdrawal-form" className="space-y-2">
      <Label htmlFor="withdrawal-amount" className="text-xs text-eh-muted">
        {t("gw.wallet.withdrawAmountLabel")}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id="withdrawal-amount"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          placeholder="0.00"
          disabled={blocked}
          className="max-w-[160px]"
          data-testid="withdrawal-amount-input"
        />
        <span className="text-sm text-eh-muted uppercase">{currency}</span>
        <Button
          type="button"
          size="sm"
          onClick={openConfirm}
          disabled={blocked || destinationBlocked || !validAmount}
          data-testid="withdrawal-open-confirm"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {t("gw.wallet.requestWithdrawal")}
        </Button>
      </div>

      {blocked && (
        <p className="text-xs text-rose-600" data-testid="withdrawal-frozen-note">
          {t("gw.wallet.frozenBlocksWithdrawal")}
        </p>
      )}
      {/* Rendered on an UNTOUCHED form — it is the answer to "may I withdraw at all",
          which does not depend on what has been typed. Amber, not rose: nothing is
          wrong with the practice's money, an errand is outstanding, and the link is
          that errand. `sconn.blocked.*` is reused rather than re-worded: the Connect
          card on this same page already localises this exact closed set, and a second
          wording of "Stripe has not enabled your account yet" is a second thing to
          keep true. */}
      {destinationBlocked && (
        <p className="text-xs text-amber-700" role="alert" data-testid="withdrawal-destination-not-ready">
          {t("gw.wallet.destinationNotReady")}
          {destinationBlockedReason && (
            <span className="block" data-testid="withdrawal-destination-reason">
              {t(`sconn.blocked.${destinationBlockedReason}`, null, destinationBlockedReason)}
            </span>
          )}
          {payoutSetupHref && (
            <a
              href={payoutSetupHref}
              className="block mt-0.5 font-medium underline"
              data-testid="withdrawal-destination-setup-link"
            >
              {t("gw.wallet.destinationSetupCta")}
            </a>
          )}
        </p>
      )}
      {!blocked && belowMinimum && (
        <p className="text-xs text-amber-700" data-testid="withdrawal-below-minimum">
          {t("gw.wallet.belowMinimum", { min: fmtMoney(minPayoutCents, currency) })}
        </p>
      )}
      {!blocked && aboveAvailable && (
        <p className="text-xs text-rose-600" data-testid="withdrawal-above-available">
          {t("gw.wallet.aboveAvailable", { available: fmtMoney(availableCents, currency) })}
        </p>
      )}
      {!blocked && !belowMinimum && !aboveAvailable && expectsReview && (
        <p className="text-xs text-blue-700" data-testid="withdrawal-expects-review">
          {t("gw.wallet.expectsReview", { max: fmtMoney(autoApproveMaxCents, currency) })}
        </p>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!submitting) setDialogOpen(o); }}>
        <DialogContent data-testid="withdrawal-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("gw.wallet.confirmTitle")}</DialogTitle>
            <DialogDescription>{t("gw.wallet.confirmDesc")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-eh-cream px-4 py-4 text-center">
            <div className="text-3xl font-bold text-eh-ink tabular-nums">
              {fmtMoney(amountCents, currency)}
            </div>
          </div>
          {(disclosesReserve || crossBorder || conversion) && (
            <div className="space-y-1 text-xs text-eh-muted" data-testid="withdrawal-disclosure">
              {disclosesReserve && (
                <p data-testid="withdrawal-receive-now">
                  {t("gw.wallet.receiveNow", { amount: fmtMoney(receiveNowCents, currency) })}
                </p>
              )}
              {disclosesReserve && (
                <p data-testid="withdrawal-reserve-held">
                  {t("gw.wallet.reserveHeld", {
                    amount: fmtMoney(reserveHeldCents, currency),
                    pct: reservePct,
                    date: new Date(reserveWindowEndsAt).toLocaleDateString(dateLocale(lang)),
                  })}
                </p>
              )}
              {crossBorder && (
                <p data-testid="withdrawal-cross-border-note">
                  {t("gw.wallet.crossBorderNote")}
                </p>
              )}
              {conversion?.estimate && (
                <p data-testid="withdrawal-conversion-estimate">
                  {t("gw.wallet.conversionEstimate", {
                    currency: localCode,
                    gross: fmtMoney(conversion.estimate.grossCents, conversion.currency),
                    fee: fmtMoney(conversion.estimate.feeCents, conversion.currency),
                    pct: conversionPct,
                    net: fmtMoney(conversion.estimate.netCents, conversion.currency),
                  })}
                </p>
              )}
              {conversion && !conversion.estimate && (
                <p data-testid="withdrawal-conversion-no-history">
                  {t("gw.wallet.conversionNoHistory", { currency: localCode, pct: conversionPct })}
                </p>
              )}
            </div>
          )}
          {submitErrorCode && (
            <p className="text-xs text-rose-600" role="alert" data-testid="withdrawal-submit-error">
              {gatewayErrorText(submitErrorCode, tc)}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              {t("gw.wallet.cancel")}
            </Button>
            <Button onClick={confirm} disabled={submitting} data-testid="withdrawal-confirm">
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {t("gw.wallet.confirmSend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
